import { apiService } from './ApiService';
import {
  OnboardingInvitePreview,
  OnboardingSession,
  OnboardingTransform,
  OnboardingUserSearchResult
} from '../types/Onboarding';

class OnboardingService {
  async listProjects(): Promise<OnboardingSession[]> {
    return apiService.get('/onboarding/projects');
  }

  async createProject(data: { siteName: string; siteDescription?: string; metadata?: Record<string, any> }): Promise<OnboardingSession> {
    return apiService.post('/onboarding/projects', data);
  }

  async createProjectForSite(siteId: string): Promise<OnboardingSession> {
    return apiService.post(`/onboarding/sites/${siteId}/session`, {});
  }

  async getProject(sessionId: string): Promise<OnboardingSession> {
    return apiService.get(`/onboarding/projects/${sessionId}`);
  }

  async updateProject(sessionId: string, data: Record<string, any>): Promise<OnboardingSession> {
    return apiService.patch(`/onboarding/projects/${sessionId}`, data);
  }

  async searchUsers(sessionId: string, query: string): Promise<OnboardingUserSearchResult[]> {
    return apiService.get(`/onboarding/projects/${sessionId}/users/search?q=${encodeURIComponent(query)}`);
  }

  async addInvite(sessionId: string, data: {
    existingUserId?: string;
    email?: string;
    name?: string;
    organization?: string;
  }): Promise<OnboardingSession> {
    return apiService.post(`/onboarding/projects/${sessionId}/invites`, data);
  }

  async updateInvite(sessionId: string, inviteId: string, data: {
    existingUserId?: string;
    email?: string;
    name?: string;
    organization?: string;
  }): Promise<OnboardingSession> {
    return apiService.patch(`/onboarding/projects/${sessionId}/invites/${inviteId}`, data);
  }

  async deleteInvite(sessionId: string, inviteId: string): Promise<OnboardingSession> {
    return apiService.delete(`/onboarding/projects/${sessionId}/invites/${inviteId}`);
  }

  async saveAlignment(sessionId: string, transform: OnboardingTransform): Promise<OnboardingSession> {
    return apiService.put(`/onboarding/projects/${sessionId}/alignment`, transform);
  }

  async verifyAlignment(sessionId: string): Promise<OnboardingSession> {
    return apiService.post(`/onboarding/projects/${sessionId}/alignment/verify`, {});
  }

  async publishProject(sessionId: string): Promise<OnboardingSession> {
    return apiService.post(`/onboarding/projects/${sessionId}/publish`, {});
  }

  async resendEmail(sessionId: string, emailId: number): Promise<{ success: boolean; error?: string }> {
    return apiService.post(`/onboarding/projects/${sessionId}/emails/${emailId}/resend`, {});
  }

  async assignManager(sessionId: string, userId: string): Promise<OnboardingSession> {
    return apiService.post(`/onboarding/projects/${sessionId}/assignments`, { userId });
  }

  async removeManager(sessionId: string, userId: string): Promise<OnboardingSession> {
    return apiService.delete(`/onboarding/projects/${sessionId}/assignments/${userId}`);
  }

  // Pre-login flows — apiService simply sends no auth header when no token is set;
  // its error extraction (error.error || fallback) matches the previous inline handling.
  async getInvitePreview(token: string): Promise<OnboardingInvitePreview> {
    return apiService.get(`/onboarding/invites/${encodeURIComponent(token)}`);
  }

  async acceptInvite(token: string, data: { name?: string; organization?: string; password?: string }): Promise<{ success: boolean; siteId: string; siteName: string; userId: string }> {
    return apiService.post(`/onboarding/invites/${encodeURIComponent(token)}/accept`, data);
  }
}

export default new OnboardingService();
